/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { Calendar } from '@services/requests/housien-models/Calendar';

/** Brand color, used as the canonical color of the primary calendar. */
export const BRAND_COLOR = '#4a0e11';

/**
 * Fixed palette offered when creating/editing calendars. The first entry is
 * the brand color (primary calendar default); the rest are muted, warm-toned
 * accent hues that harmonise with the Housien design system (wine seed base,
 * brand beige surfaces, terracotta accent).
 */
export const CALENDAR_PALETTE: readonly string[] = [
  BRAND_COLOR,   // vinho institucional — reservado para o calendário primário
  '#8A5D4F',     // terracota rosado   — brand-accent do sistema
  '#2D5A6B',     // petróleo azulado   — sereno, profissional
  '#5C4A1E',     // âmbar terroso      — caloroso, neutro
  '#3D5A47',     // sálvia escura      — natural, discreto
  '#6B3A5E',     // ameixa muted       — elegante
  '#1E3A5F',     // marinho profundo   — corporativo
  '#7A3B1E',     // cobre quente       — família da marca
  '#4A5D2E',     // oliva profunda     — natural, sóbrio
  '#5A3242',     // bordô acinzentado  — quente, discreto
  '#2E5A55',     // verde-mar escuro   — frio, equilibrado
  '#6B4A2E',     // castanho tabaco    — terroso
  '#3A3A6B',     // índigo suave       — frio, neutro
];

/**
 * Colour of the Visitas layer. Fixed, and reserved like the brand: a personal
 * calendar painted with it would be indistinguishable from the visits the
 * sidebar toggles separately.
 */
export const VISITS_COLOR = '#8A5D4F';

/** Colours the system owns and no user calendar may take. */
export const RESERVED_COLORS: readonly string[] = [BRAND_COLOR, VISITS_COLOR];

/**
 * Palette the user may pick from when creating/editing their own calendars.
 *
 * Excludes {@link RESERVED_COLORS}: the brand belongs to the primary calendar,
 * so user calendars can never impersonate the institutional one, and the
 * Visitas colour belongs to the visits layer. What remains is the pool every
 * layer draws from — the frontend picker, the backend's join-time assignment
 * and the seed — so the three must stay in step.
 */
export const SELECTABLE_PALETTE: readonly string[] = CALENDAR_PALETTE.filter(
  (color) => !RESERVED_COLORS.some((r) => r.toLowerCase() === color.toLowerCase())
);

/** Translator signature compatible with react-i18next's `t`. */
type Translate = (key: string) => string;

/**
 * Whether a calendar is the primary (default institutional) one. Permanent,
 * undeletable, always rendered with the brand identity.
 */
export const isPrimaryCalendar = (calendar: Calendar): boolean =>
  calendar.is_primary === true;

/**
 * Display name for a calendar. The primary (institutional) calendar shows the
 * agency name when available, falling back to the localized "Default" label;
 * other calendars show their raw name.
 *
 * @param calendar The calendar to label.
 * @param t Translator.
 * @param agencyName Optional agency name used to label the primary calendar.
 * @returns The name to display.
 */
export const calendarDisplayName = (
  calendar: Calendar,
  t: Translate,
  agencyName?: string
): string => {
  if (!isPrimaryCalendar(calendar)) return calendar.name ?? '';
  return agencyName?.trim() ? agencyName.trim() : t('sidebar.primary');
};

/**
 * Resolve the display swatch color for a calendar. The primary calendar always
 * renders with the brand color, even if a stale (legacy) color is stored on it.
 * Others use their stored color, falling back to the brand color.
 */
export const calendarColor = (calendar: Calendar): string =>
  isPrimaryCalendar(calendar) ? BRAND_COLOR : calendar.color || BRAND_COLOR;
